import type { Block, ContentBundle, GradeField, Lesson, Progress, Track } from './types';

/** Progress keys are "<trackSlug>/<lessonSlug>" — the prototype's format, kept verbatim. */
export const lessonKey = (trackId: string, lessonId: string) => `${trackId}/${lessonId}`;
export const checklistKey = (checklistId: string, index: number) => `${checklistId}${index}`;

export interface FlatLesson {
  track: Track;
  lesson: Lesson;
}

/** Every lesson in reading order, with its track — the basis for search and "next up". */
export function flatten(tracks: Track[]): FlatLesson[] {
  return tracks.flatMap((track) => track.lessons.map((lesson) => ({ track, lesson })));
}

export function countRead(tracks: Track[], done: Progress['done']): number {
  return flatten(tracks).filter(({ track, lesson }) => done[lessonKey(track.id, lesson.id)]).length;
}

export function totalLessons(tracks: Track[]): number {
  return tracks.reduce((sum, t) => sum + t.lessons.length, 0);
}

export function trackMinutes(track: Track): number {
  return track.lessons.reduce((sum, l) => sum + l.mins, 0);
}

/** The first unread lesson in curriculum order, or null when everything is read. */
export function nextUp(tracks: Track[], done: Progress['done']): FlatLesson | null {
  return flatten(tracks).find(({ track, lesson }) => !done[lessonKey(track.id, lesson.id)]) ?? null;
}

/** Flatten a block's text so search can match on lesson bodies, not just titles. */
function blockText(block: Block): string {
  switch (block.t) {
    case 'p':
    case 'h':
      return block.x;
    case 'ul':
    case 'ol':
      return block.items.join(' ');
    case 'note':
      return `${block.title} ${block.x}`;
    case 'do':
      return [...block.dos, ...block.donts].join(' ');
    case 'stat':
      return block.items.map((i) => `${i.k} ${i.v}`).join(' ');
    case 'quote':
      return `${block.x} ${block.who}`;
  }
}

/**
 * All query terms must match somewhere in the lesson (AND, not OR), with a naive plural
 * fallback so "listings" finds "listing". Mirrors the prototype's behaviour.
 */
export function search(tracks: Track[], query: string): FlatLesson[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];

  return flatten(tracks).filter(({ track, lesson }) => {
    const hay = [track.name, lesson.title, lesson.summary, ...lesson.blocks.map(blockText)]
      .join(' ')
      .toLowerCase();
    return terms.every(
      (t) => hay.includes(t) || (t.length > 3 && hay.includes(t.replace(/s$/, ''))),
    );
  });
}

export interface GradeResult {
  score: number;
  tone: 'good' | 'mid' | 'bad';
  verdict: string;
  gaps: GradeField[];
}

/**
 * Weighted listing score out of 100. Weights are validated to total 100 at the admin
 * boundary, so the score is a true percentage; gaps are ordered by weight so the vendor
 * fixes the most damaging omission first.
 */
export function gradeListing(fields: GradeField[], grade: Progress['grade']): GradeResult {
  const score = fields.reduce((sum, f) => (grade[f.id] ? sum + f.weight : sum), 0);
  const tone: GradeResult['tone'] = score >= 85 ? 'good' : score >= 60 ? 'mid' : 'bad';

  const verdict =
    tone === 'good'
      ? 'Ready to publish, and eligible for placement.'
      : tone === 'mid'
        ? 'This will sell, but it is leaking conversion. Close the gaps below.'
        : 'Do not publish yet — a buyer cannot make a decision from this.';

  const gaps = fields.filter((f) => !grade[f.id]).sort((a, b) => b.weight - a.weight);

  return { score, tone, verdict, gaps };
}

export function findTrack(content: ContentBundle, trackId: string): Track | undefined {
  return content.tracks.find((t) => t.id === trackId);
}

export function findLesson(track: Track | undefined, lessonId: string): Lesson | undefined {
  return track?.lessons.find((l) => l.id === lessonId);
}
